/**
 * Text on the picture: what a title looks like and how it is laid out.
 *
 * A text clip has no file behind it. Everything it needs is in the project
 * record — the words, the style, the box — so it survives a reload with
 * nothing to relink, and it is drawn by the same renderFrame the video goes
 * through, so preview and export agree on it to the pixel.
 *
 * Sizes are fractions of the composition, like every other measurement in the
 * project: a title at 7% of the frame's shorter side is the same title in the 480p
 * preview and the 4K export.
 */

import type { Project } from "./timeline.ts";

/**
 * The families on offer. They are the three the app already ships for its own
 * interface, plus one that leans on whatever heavy face the machine has: the
 * export is rendered on the same machine as the preview, so a system font is
 * safe here in a way it would not be on a server.
 */
export type TextFont = "sans" | "serif" | "mono" | "display";

export interface FontFace {
  label: string;
  family: string;
  /** The weights actually loaded, so a bold that would be synthesised is not offered as real. */
  regular: number;
  bold: number;
}

export const TEXT_FONTS: Record<TextFont, FontFace> = {
  sans: { label: "Sans", family: '"Manrope", ui-sans-serif, system-ui, sans-serif', regular: 400, bold: 700 },
  serif: { label: "Serif", family: '"Fraunces", "Iowan Old Style", Georgia, serif', regular: 400, bold: 600 },
  mono: { label: "Mono", family: '"IBM Plex Mono", ui-monospace, Menlo, monospace', regular: 400, bold: 500 },
  display: { label: "Display", family: 'Impact, "Arial Black", "Helvetica Neue", sans-serif', regular: 700, bold: 700 },
};

export type TextAlign = "left" | "center" | "right";
export type TextValign = "top" | "middle" | "bottom";

export interface TextBackground {
  color: string;
  /** 0..1. Kept apart from the colour so a swatch can be chosen and then faded. */
  opacity: number;
  /** Padding around the words, as a multiple of the font size. */
  padding: number;
}

export interface TextOutline {
  color: string;
  /** Stroke width as a multiple of the font size. 0.08 is the usual subtitle look. */
  width: number;
}

export interface TextStyle {
  font: TextFont;
  bold: boolean;
  italic: boolean;
  /**
   * Font size as a fraction of the composition's shorter side. The shorter
   * side rather than the height so that a title reads the same size in a
   * Reel as in a landscape frame: 7% of 1080 either way, instead of 7% of
   * 1920 the moment the frame turns upright.
   */
  size: number;
  color: string;
  align: TextAlign;
  valign: TextValign;
  /** Line height as a multiple of the font size. */
  lineHeight: number;
  /** A soft drop shadow, for legibility over footage. */
  shadow: boolean;
  outline: TextOutline | null;
  background: TextBackground | null;
}

export const DEFAULT_TEXT_STYLE: TextStyle = {
  font: "sans",
  bold: true,
  italic: false,
  size: 0.07,
  color: "#ffffff",
  align: "center",
  valign: "middle",
  lineHeight: 1.2,
  shadow: true,
  outline: null,
  background: null,
};

/**
 * Ready-made looks. Each is a whole style rather than a tweak, so pressing one
 * gives a known result from anywhere; the individual controls are for going on
 * from there.
 */
export const TEXT_LOOKS: { id: string; label: string; style: TextStyle }[] = [
  { id: "clean", label: "Clean", style: DEFAULT_TEXT_STYLE },
  {
    id: "boxed",
    label: "Boxed",
    style: {
      ...DEFAULT_TEXT_STYLE,
      shadow: false,
      background: { color: "#14100c", opacity: 0.75, padding: 0.4 },
    },
  },
  {
    id: "outlined",
    label: "Outlined",
    style: {
      ...DEFAULT_TEXT_STYLE,
      font: "display",
      shadow: false,
      outline: { color: "#000000", width: 0.1 },
    },
  },
  {
    id: "highlight",
    label: "Highlight",
    style: {
      ...DEFAULT_TEXT_STYLE,
      color: "#14100c",
      shadow: false,
      background: { color: "#ffd23f", opacity: 1, padding: 0.3 },
    },
  },
  {
    id: "serif",
    label: "Serif",
    style: { ...DEFAULT_TEXT_STYLE, font: "serif", bold: false, italic: true, size: 0.08 },
  },
];

/** Colours worth one click. Everything else is the colour picker. */
export const TEXT_SWATCHES = ["#ffffff", "#14100c", "#ffd23f", "#f25f5c", "#4f8496", "#a6b58f"];

/** The px size of the font in composition pixels. */
export const fontPixels = (style: TextStyle, project: Project): number =>
  Math.max(1, style.size * Math.min(project.width, project.height));

/** A CSS font shorthand for the canvas, at a given pixel size. */
export function fontString(style: TextStyle, px: number): string {
  const face = TEXT_FONTS[style.font];
  const weight = style.bold ? face.bold : face.regular;
  return `${style.italic ? "italic " : ""}${weight} ${px}px ${face.family}`;
}

/**
 * Measuring is the one thing here that needs a browser. It is injected so the
 * layout can be tested in node, and so the renderer can hand in the context it
 * is about to draw with rather than keeping a second one warm.
 */
export type Measure = (text: string, font: string) => number;

let shared: CanvasRenderingContext2D | null | undefined;
function sharedContext(): CanvasRenderingContext2D | null {
  if (shared !== undefined) return shared;
  try {
    shared = typeof document === "undefined" ? null : document.createElement("canvas").getContext("2d");
  } catch {
    shared = null;
  }
  return shared;
}

/** Roughly half an em per character, which is what a sans face averages. */
const approximate: Measure = (text, font) => {
  const px = Number(/(\d+(?:\.\d+)?)px/.exec(font)?.[1] ?? 16);
  return text.length * px * 0.52;
};

export const defaultMeasure: Measure = (text, font) => {
  const ctx = sharedContext();
  if (!ctx) return approximate(text, font);
  ctx.font = font;
  return ctx.measureText(text).width;
};

/** For tests and for anything that wants to measure without a canvas. */
export function setMeasureForTests(measure: Measure | null): void {
  override = measure;
}
let override: Measure | null = null;
const measureWith = (measure?: Measure): Measure => measure ?? override ?? defaultMeasure;

export interface TextLayout {
  lines: string[];
  /** Composition pixels. */
  fontPx: number;
  lineHeightPx: number;
  paddingPx: number;
  /** The widest line, without padding. */
  textWidth: number;
  /** Lines plus padding: what the box needs to be to hold the words. */
  height: number;
  font: string;
}

/**
 * Wrap the words into a box of the given width.
 *
 * Hard newlines are kept: a person who pressed Enter meant it. A single word
 * wider than the box is left whole and overflows, because breaking it in the
 * middle would be worse than showing what happened.
 */
export function layoutText(
  content: string,
  style: TextStyle,
  project: Project,
  boxWidthPx: number,
  measure?: Measure,
): TextLayout {
  const measureText = measureWith(measure);
  const fontPx = fontPixels(style, project);
  const font = fontString(style, fontPx);
  const paddingPx = (style.background?.padding ?? 0) * fontPx;
  const maxWidth = Math.max(1, boxWidthPx - paddingPx * 2);

  const lines: string[] = [];
  for (const paragraph of content.replace(/\r/g, "").split("\n")) {
    const words = paragraph.split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      lines.push("");
      continue;
    }
    let current = "";
    for (const word of words) {
      const candidate = current ? `${current} ${word}` : word;
      if (current && measureText(candidate, font) > maxWidth) {
        lines.push(current);
        current = word;
      } else {
        current = candidate;
      }
    }
    lines.push(current);
  }

  const lineHeightPx = fontPx * style.lineHeight;
  const textWidth = lines.reduce((widest, line) => Math.max(widest, measureText(line, font)), 0);
  return {
    lines,
    fontPx,
    lineHeightPx,
    paddingPx,
    textWidth,
    height: lines.length * lineHeightPx + paddingPx * 2,
    font,
  };
}

/**
 * The words' own height, as a fraction of the composition. This is what a text
 * box is tall when nobody has said otherwise.
 */
export function autoTextHeight(
  content: string,
  style: TextStyle,
  project: Project,
  widthFraction: number,
  measure?: Measure,
): number {
  const layout = layoutText(content, style, project, widthFraction * project.width, measure);
  return Math.max(0.01, layout.height / project.height);
}

/**
 * The width the box would need to hold its widest line on one row, as a
 * fraction of the composition. For the "shrink the box to the words" button.
 */
export function fittedTextWidth(
  content: string,
  style: TextStyle,
  project: Project,
  measure?: Measure,
): number {
  // Laid out at the full frame width so nothing wraps that does not have to.
  const layout = layoutText(content, style, project, project.width * 4, measure);
  // A hair over, so the measurement's own rounding cannot wrap the last word.
  return Math.min(1, (layout.textWidth * 1.02 + layout.paddingPx * 2) / project.width);
}

const requested = new Set<string>();

/**
 * Ask the browser to have a face ready.
 *
 * A canvas draws with whatever is loaded *now*: the first frame of a title in
 * a face nobody has used yet comes out in the fallback. The preview redraws
 * every frame so it corrects itself a moment later; the export waits on the
 * returned promise so its first frame is already right.
 */
export function loadFont(style: TextStyle): Promise<unknown> {
  if (typeof document === "undefined" || !("fonts" in document)) return Promise.resolve();
  const font = fontString(style, 32);
  if (requested.has(font)) return document.fonts.ready;
  requested.add(font);
  return document.fonts.load(font).catch(() => undefined);
}
